export interface Question {
  question: string;
  options: { text: string; value: string }[];
}

export interface Tool {
  name: string;
  description: string;
  url: string;
  workflows?: string[];
}

export const WORKFLOW_ADJECTIVES = {
  W: {
    adjective: "Pragmatic",
    description: "You value tools that seamlessly integrate into your existing processes.",
  },
  X: {
    adjective: "Ambitious",
    description: "You seek tools that help you level up and expand your capabilities.",
  },
  Y: {
    adjective: "Efficient",
    description: "You prioritize tools that eliminate repetitive work from your day.",
  },
  Z: {
    adjective: "Curious",
    description: "You appreciate tools that help you explore and learn new concepts.",
  },
};

export const PERSONALITY_QUESTIONS: Question[] = [
  {
    question: "When you hear 'AI' in event planning, what comes to mind first?",
    options: [
      { text: "⚙️ A tool that can automate the heavy lifting behind the scenes", value: "A" },
      { text: "🎨 Something creative to help me design or visualize concepts", value: "B" },
      { text: "📣 A smarter way to market my events and wow attendees", value: "C" },
      { text: "🔍 A new helper I'm curious to test out", value: "D" },
      { text: "🤷 Not sure yet – I've seen the buzz but haven't tried it", value: "E" },
    ],
  },
  {
    question: "You've got a huge guest list to manage. What sounds most appealing?",
    options: [
      { text: "🔄 Building a system that auto-updates RSVPs and reminders", value: "A" },
      { text: "✨ Finding a no-fuss tool that does the heavy lifting for me", value: "B" },
      { text: "💌 Using AI to personalize invitations and communications", value: "C" },
      { text: "📝 Just trying an easy step like copying contacts into a template", value: "D" },
      { text: "📊 Sticking with my trusted spreadsheets and manual check-ins", value: "E" },
    ],
  },
  {
    question: "What part of event planning would you most love to offload to AI?",
    options: [
      { text: "💼 Logistics like budgets, contracts, or vendor coordination", value: "A" },
      { text: "💡 Creative brainstorming for themes, layouts, or activations", value: "B" },
      { text: "📱 Crafting social posts, campaigns, or recaps", value: "C" },
      { text: "⏰ Generating timelines or checklists to stay on track", value: "D" },
      { text: "🙌 None – I'd rather rely on people and my own process", value: "E" },
    ],
  },
  {
    question: "When you're faced with a brand-new tool, your reaction is…",
    options: [
      { text: "🏊 Dive in, test it, maybe even customize it", value: "A" },
      { text: "🌟 See how I can use it to bring my ideas to life without too much setup", value: "B" },
      { text: "📈 Figure out how it could make my marketing or storytelling better", value: "C" },
      { text: "👶 Try the simplest feature first just to see if it helps", value: "D" },
      { text: "⏳ Wait until someone I trust swears by it", value: "E" },
    ],
  },
  {
    question: "You're on a tight deadline. Where would you want AI's help?",
    options: [
      { text: "🚀 Automating repetitive tasks so I can focus on strategy", value: "A" },
      { text: "🎨 Quickly mocking up a new concept or experience", value: "B" },
      { text: "✍️ Drafting copy, decks, or visuals that look polished", value: "C" },
      { text: "💭 Suggesting a starting point so I'm not staring at a blank page", value: "D" },
      { text: "💪 Honestly, I'd just push through myself", value: "E" },
    ],
  },
  {
    question: "If AI could 'magically' solve one headache for you, what would it be?",
    options: [
      { text: "💰 Tracking spend, invoices, and vendor communications", value: "A" },
      { text: "🏛️ Visualizing a new event space design before I book it", value: "B" },
      { text: "🎬 Producing highlight reels and campaign content instantly", value: "C" },
      { text: "📝 Organizing my to-dos into a clear, easy list", value: "D" },
      { text: "⏰ Making sure my team shows up on time", value: "E" },
    ],
  },
  {
    question: "Which of these feels most like your style when planning?",
    options: [
      { text: "🔧 Technical – I like systems, tools, and structure", value: "A" },
      { text: "🎨 Creative – I want to make things look and feel amazing", value: "B" },
      { text: "📚 Story-driven – I focus on how the event is experienced and remembered", value: "C" },
      { text: "⚡ Practical – I just want things to be simpler and faster", value: "D" },
      { text: "📼 Old-school – I've got my process and I trust it", value: "E" },
    ],
  },
  {
    question: "How do you usually discover new tools or tricks for your work?",
    options: [
      { text: "🛠️ By tinkering with software or experimenting myself", value: "A" },
      { text: "👥 Through professional communities or industry groups", value: "B" },
      { text: "📰 From marketing blogs, LinkedIn posts, or campaigns I admire", value: "C" },
      { text: "🤝 A friend or colleague shows me something easy to try", value: "D" },
      { text: "⭐ From recommendations shared by people I trust", value: "E" },
    ],
  },
  {
    question: "Imagine you're planning the biggest event of your career. How would AI fit in?",
    options: [
      { text: "🏗️ I'd build custom workflows to keep every detail organized", value: "A" },
      { text: "✨ I'd use it to brainstorm creative touches that wow", value: "B" },
      { text: "🚀 I'd have it generate killer marketing campaigns and recaps", value: "C" },
      { text: "📋 I'd lean on it for task lists and slide decks to stay sane", value: "D" },
      { text: "🚫 I probably wouldn't use it – too risky for something this important", value: "E" },
    ],
  },
  {
    question: "How do you decide if an AI tool is worth keeping in your toolkit?",
    options: [
      { text: "⏱️ It integrates smoothly and saves me serious time", value: "A" },
      { text: "🌟 It helps me create something new I couldn't do before", value: "B" },
      { text: "🎯 It makes my marketing or storytelling stand out", value: "C" },
      { text: "🔓 It's simple, easy, and gets me unstuck", value: "D" },
      { text: "✅ It's proven, reliable, and doesn't just chase hype", value: "E" },
    ],
  },
  {
    question: "Finally, how do you want to use AI in your workflow?",
    options: [
      { text: "🧩 Fill the gaps in my current workflows", value: "W" },
      { text: "📈 Enhance and expand my existing skills", value: "X" },
      { text: "🤖 Automate tasks that I don't want to do", value: "Y" },
      { text: "🎓 As a teacher to learn new ideas and concepts", value: "Z" },
    ],
  },
];

export const PERSONALITY_TYPES = {
  A: {
    title: "The Architect",
    description:
      "You're the kind of person who looks at an AI model and immediately thinks about what you could build on top of it. APIs and SDKs don't scare you – in fact, they're your playground. You're always scanning for the latest research drops and tools that make experimenting faster.",
    emoji: "🏗️",
    suggestedTools: [
      {
        name: "Zapier",
        description: "build automations between your event tools without touching code",
        url: "https://zapier.com",
        workflows: ["W", "Y"],
      },
      {
        name: "Sparkit",
        description: "AI planning and task management",
        url: "https://sparkit.ai",
        workflows: ["W", "Y"],
      },
      {
        name: "HubSpot CRM + ChatSpot",
        description: "manage attendees, partners, and communication in one place",
        url: "https://hubspot.com",
        workflows: ["W", "Y"],
      },
      {
        name: "Make.com",
        description: "drag-and-drop automations for workflows",
        url: "https://make.com",
        workflows: ["W", "Y"],
      },
    ],
    copyPasta: `🏗️ THE ARCHITECT

I just took the AI Personality Test and apparently I'm "The Architect" – someone who looks at AI and immediately thinks about what to build on top of it!

My recommended tools:
• Zapier – build automations between event tools
• Sparkit – AI planning and task management
• HubSpot CRM + ChatSpot – manage attendees & communication
• Make.com – drag-and-drop workflow automations

Take the test yourself: nowadays.ai/games/personality-quiz`,
  },
  B: {
    title: "Creator",
    description:
      "You may not write code, but that doesn't stop you from making things happen. You're scrappy, curious, and always ready to test a new platform if it helps bring your ideas to life. You want AI that feels like building blocks – simple enough to pick up, powerful enough to make something real.",
    emoji: "🎨",
    suggestedTools: [
      {
        name: 'Claude\'s "Art of Prompting"',
        description: "strong for structured workflows",
        url: "https://claude.ai",
        workflows: ["X", "Z"],
      },
      {
        name: "Canva Magic Studio",
        description: "graphics, templates, and AI-powered design",
        url: "https://canva.com",
        workflows: ["X"],
      },
      {
        name: "Gamma AI",
        description: "generate polished slides and presentations in minutes",
        url: "https://gamma.app",
        workflows: ["Y", "X"],
      },
      {
        name: "Lexica",
        description: "fresh image generation for visuals and themes",
        url: "https://lexica.art",
        workflows: ["X"],
      },
      {
        name: "HeyGen",
        description: "create avatars and videos to scale event content",
        url: "https://heygen.com",
        workflows: ["X", "Y"],
      },
    ],
    copyPasta: `🎨 THE CREATOR

I just took the AI Personality Test and I'm "The Creator" – scrappy, curious, and ready to test new platforms that help bring ideas to life!

My recommended tools:
• Claude's "Art of Prompting" for structured workflows
• Canva Magic Studio – AI-powered design & graphics
• Gamma AI – polished slides in minutes
• Lexica/Ideogram.ai – fresh image generation
• HeyGen – create avatars and videos at scale

Take the test yourself: nowadays.ai/games/personality-quiz`,
  },
  C: {
    title: "Strategist",
    description:
      "You know how to turn AI into an amplifier for your brand and message. Whether it's content calendars, social posts, or event campaigns, you're already putting AI to work. What you want now is more originality – tools that help you stand out, not blend in.",
    emoji: "🎯",
    suggestedTools: [
      {
        name: "Buffer AI Assistant",
        description: "social scheduling and smart captions",
        url: "https://buffer.com",
        workflows: ["Y", "X"],
      },
      {
        name: "Copy.ai",
        description: "campaign ideas and content generation",
        url: "https://copy.ai",
        workflows: ["X", "Y"],
      },
      {
        name: "Mailchimp Creative Assistant",
        description: "personalized email campaigns at scale",
        url: "https://mailchimp.com",
        workflows: ["Y", "X"],
      },
      {
        name: "Opus AI",
        description: "cut event footage into engaging clips for socials",
        url: "https://opus.pro",
        workflows: ["Y", "X"],
      },
    ],
    copyPasta: `🎯 THE STRATEGIST

I just took the AI Personality Test and I'm "The Strategist" – someone who turns AI into an amplifier for brand and message!

My recommended tools:
• Buffer AI Assistant – social scheduling & smart captions
• Copy.ai – campaign ideas and content generation
• Mailchimp Creative Assistant – personalized email campaigns
• Opus AI – cut event footage into engaging clips

Take the test yourself: nowadays.ai/games/personality-quiz`,
  },
  D: {
    title: "Explorer",
    description:
      "You're curious about AI but haven't gone too deep yet. You're not looking to become a developer overnight – you just want approachable tools that help you get things done without the overwhelm. The right tools will make AI feel less like a buzzword and more like an everyday helper.",
    emoji: "🧭",
    suggestedTools: [
      {
        name: "ChatGPT",
        description: "simple brainstorms, drafts, or checklists",
        url: "https://chatgpt.com",
        workflows: ["W", "Z"],
      },
      {
        name: "NotebookLM",
        description: "AI assistant built around your own notes",
        url: "https://notebooklm.google.com",
        workflows: ["W", "Z"],
      },
      {
        name: "Beautiful.ai",
        description: "auto-polished presentations without starting from scratch",
        url: "https://beautiful.ai",
        workflows: ["Y", "X"],
      },
      {
        name: "Perplexity",
        description: "quick research and inspiration",
        url: "https://perplexity.ai",
        workflows: ["Z", "W"],
      },
    ],
    copyPasta: `🧭 THE EXPLORER

I just took the AI Personality Test and I'm "The Explorer" – curious about AI but looking for approachable tools that help without the overwhelm!

My recommended tools:
• ChatGPT – simple brainstorms, drafts, or checklists
• NotebookLM – AI assistant built around your notes
• Beautiful.ai – auto-polished presentations
• Perplexity – quick research and inspiration

Take the test yourself: nowadays.ai/games/personality-quiz`,
  },
  E: {
    title: "Skeptic",
    description:
      "You're not sold on the hype – and that's fair. You'd rather see AI prove itself in ways that actually save you time and effort. When AI feels invisible and useful, you're more likely to lean in.",
    emoji: "🤔",
    suggestedTools: [
      {
        name: "Midjourney",
        description: "generate mood boards or quick visuals for themes and event concepts",
        url: "https://midjourney.com",
        workflows: ["X"],
      },
      {
        name: "Superhuman",
        description: "AI-powered email inbox for RSVPs, vendor replies, and client communication",
        url: "https://superhuman.com",
        workflows: ["Y", "W"],
      },
      {
        name: "Tidio AI Chatbot",
        description: "automate attendee FAQs on registration sites",
        url: "https://tidio.com",
        workflows: ["Y"],
      },
      {
        name: "Verbit.ai",
        description: "real-time transcription for panels and meetings",
        url: "https://verbit.ai",
        workflows: ["Y", "W"],
      },
    ],
    copyPasta: `🤔 THE SKEPTIC

I just took the AI Personality Test and I'm "The Skeptic" – not sold on the hype, but ready for AI that actually saves time and effort!

My recommended tools:
• Midjourney – generate mood boards & visuals
• Superhuman – AI-powered email for RSVPs & vendor replies
• Tidio AI Chatbot – automate attendee FAQs
• Verbit.ai – real-time transcription for meetings

Take the test yourself: nowadays.ai/games/personality-quiz`,
  },
};
